import { Controller } from '@hotwired/stimulus'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SFetch from '@app/utils/sfetch'

// Connects to data-controller="setup-company-admin-sync"
export default class extends Controller {
  static targets = [
    // Main UI containers
    'adminSyncForm',
    'syncStatus',
    'errorMessageWrapper',
    'errorMessage',
    'companyDetailsForm',

    // Success/Loading display elements
    'successSyncName',
    'successSyncReference',
    'editButton',
    'statusBadgeSuccess',
    'statusBadgeLoading',

    // Form elements
    'syncReferenceWrapper',
    'submitButton',

    // Input fields for company details
    'nameInput',
    'fileNumberInput',
    'organisationFormInput',
    'registrationNumberInput',
    'vatNumberInput',
    'streetInput',
    'cityInput',
    'countryInput',
    'currencyInput',
  ]

  static values = {
    companyId: String,
    firmId: String,
    hasAdminSyncSetup: Boolean,
  }
  static I18n = useI18n()
  companyIdValue!: string
  firmIdValue!: string
  hasAdminSyncSetupValue!: boolean

  private adminSyncFormTarget!: HTMLElement
  private syncStatusTarget!: HTMLElement
  private errorMessageWrapperTarget!: HTMLElement
  private errorMessageTarget!: HTMLElement
  private companyDetailsFormTarget!: HTMLElement
  private successSyncNameTarget!: HTMLElement
  private successSyncReferenceTarget!: HTMLElement
  private editButtonTarget!: HTMLElement
  private statusBadgeSuccessTarget!: HTMLElement
  private statusBadgeLoadingTarget!: HTMLElement
  private syncReferenceWrapperTarget!: HTMLElement
  private submitButtonTarget!: HTMLButtonElement
  private nameInputTarget?: HTMLInputElement
  private fileNumberInputTarget?: HTMLInputElement
  private organisationFormInputTarget?: HTMLInputElement
  private registrationNumberInputTarget?: HTMLInputElement
  private vatNumberInputTarget?: HTMLInputElement
  private streetInputTarget?: HTMLInputElement
  private cityInputTarget?: HTMLInputElement
  private countryInputTarget?: HTMLSelectElement
  private currencyInputTarget?: HTMLSelectElement
  private observer?: MutationObserver

  connect(): void {
    this.updateSubmitButtonState()
    this.observeReferenceListLoading()

    // If there's a sync entity selected with a reference, fetch company data
    if (this.hasAdminSyncSetupValue) {
      void this.fetchAndPopulateCompanyData(true) // skipResetOnFailure = true
    }
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  editAdminSync(event: Event): void {
    event.preventDefault()
    this.hide(this.errorMessageWrapperTarget)
    this.hide(this.syncStatusTarget)
    this.hide(this.companyDetailsFormTarget)
    this.show(this.adminSyncFormTarget)
    this.updateSubmitButtonState()
  }

  async setupAdminSync(event: Event): Promise<void> {
    event.preventDefault()
    this.hide(this.errorMessageWrapperTarget)

    const { adminSyncId, syncReference } = this.extractFormDetails()

    this.showLoading(adminSyncId, syncReference)

    try {
      await this.updateAdminSync(adminSyncId, syncReference)

      if (adminSyncId !== '') {
        await this.fetchAndPopulateCompanyData()
      }

      this.handleSuccessfulSetup(adminSyncId, syncReference)
    } catch {
      this.handleSetupFailure()
    }
  }

  async adminSyncSelected(): Promise<void> {
    this.resetCompanyDetailsForm()
    this.hide(this.errorMessageWrapperTarget)
    this.submitButtonTarget.disabled = true

    const selectedRadio = this.adminSyncFormTarget.querySelector(
      'input[name="company[administration_sync_entity_id]"]:checked'
    ) as HTMLInputElement
    const isNoneSelected = selectedRadio && selectedRadio.value === ''

    if (isNoneSelected) {
      this.submitButtonTarget.classList.add('tw-hidden')
      this.show(this.companyDetailsFormTarget)

      if (this.hasAdminSyncSetupValue) {
        try {
          await this.updateAdminSync('', '')
          this.handleSuccessfulSetup('', '')
          this.hasAdminSyncSetupValue = false
        } catch {
          this.handleSetupFailure()
        }
      }
    } else {
      if (selectedRadio.dataset.needsReference === 'false') {
        this.submitButtonTarget.disabled = false
      }

      // we should always show that button in that case
      // as the update state is not always triggered
      // like when we generate an open field for sync references
      this.submitButtonTarget.classList.remove('tw-hidden')

      this.hide(this.companyDetailsFormTarget)
    }
  }

  updateSubmitButtonState(): void {
    const selectedRadio = this.adminSyncFormTarget.querySelector(
      'input[name="company[administration_sync_entity_id]"]:checked'
    ) as HTMLInputElement
    const isProviderSelected = selectedRadio && selectedRadio.value !== ''

    this.submitButtonTarget.classList.toggle('tw-hidden', !isProviderSelected)

    if (isProviderSelected) {
      const syncReferenceField = this.syncReferenceWrapperTarget.querySelector(
        'input, select'
      ) as HTMLInputElement | HTMLSelectElement

      const isReferenceEntered =
        !syncReferenceField ||
        (syncReferenceField && syncReferenceField.value !== '')

      this.submitButtonTarget.disabled = !isReferenceEntered
    } else {
      this.submitButtonTarget.disabled = true
    }
  }

  private hide(element: HTMLElement): void {
    element.classList.add('tw-hidden')
  }
  private show(element: HTMLElement): void {
    element.classList.remove('tw-hidden')
  }

  private observeReferenceListLoading(): void {
    // used to detect when select2 is initialized because events are not fired
    this.observer = new MutationObserver(() => {
      if (this.syncReferenceWrapperTarget.querySelector('.select2-container')) {
        this.updateSubmitButtonState()
      }
    })

    // Start observing the sync reference wrapper for Select2 initialization
    this.observer.observe(this.syncReferenceWrapperTarget, {
      childList: true,
      subtree: true,
    })
  }

  private showLoading(adminSyncId: string, syncReference: string): void {
    this.hide(this.errorMessageWrapperTarget)
    this.hide(this.adminSyncFormTarget)
    this.hide(this.editButtonTarget)
    this.hide(this.statusBadgeSuccessTarget)
    this.hide(this.companyDetailsFormTarget)

    // Update sync info
    this.successSyncNameTarget.textContent =
      this.getSelectedAdminSyncName(adminSyncId)
    this.successSyncReferenceTarget.textContent = syncReference

    this.show(this.statusBadgeLoadingTarget)
    this.show(this.syncStatusTarget)
  }

  private showSuccess(syncName: string, syncReference: string): void {
    this.successSyncNameTarget.textContent = syncName
    this.successSyncReferenceTarget.textContent = syncReference

    this.hide(this.statusBadgeLoadingTarget)
    this.hide(this.adminSyncFormTarget)
    this.show(this.syncStatusTarget)
    this.show(this.statusBadgeSuccessTarget)
    this.show(this.editButtonTarget)
    this.show(this.companyDetailsFormTarget)
  }

  private async updateAdminSync(
    adminSyncId: string,
    syncReference: string
  ): Promise<void> {
    const url = window.Silverfin.Routes.wizard_v2_company_admin_sync_path(
      this.firmIdValue,
      this.companyIdValue
    )

    const response = await SFetch.json(url, {
      method: 'PATCH',
      body: {
        company: {
          administration_sync_entity_id: adminSyncId || null,
          administration_sync_reference: syncReference || null,
        },
      },
    })
    if (!response) {
      throw new Error('Admin Sync Setup Failed')
    }
  }

  private async fetchCompanyData(): Promise<Record<string, string>> {
    const url =
      window.Silverfin.Routes.company_data_wizard_v2_company_admin_sync_path(
        this.firmIdValue,
        this.companyIdValue
      )

    const response = await SFetch.json(url)

    if (!response) {
      throw new Error('Fetching company data failed')
    }

    return response as Record<string, string>
  }

  private handleSuccessfulSetup(
    adminSyncId: string,
    syncReference: string
  ): void {
    this.showSuccess(this.getSelectedAdminSyncName(adminSyncId), syncReference)

    if (adminSyncId === '') {
      this.hide(this.syncStatusTarget)
      this.show(this.adminSyncFormTarget)
      this.updateSubmitButtonState()
      this.show(this.companyDetailsFormTarget)
    }
  }

  private handleSetupFailure(): void {
    this.show(this.adminSyncFormTarget)
    this.hide(this.syncStatusTarget)

    this.resetCompanyDetailsForm()

    this.errorMessageTarget.textContent = useI18n().t(
      'wizard_v2.companies.set_admin_sync.setup_failed'
    )
    this.errorMessageWrapperTarget.classList.remove('tw-hidden')
    this.companyDetailsFormTarget.classList.remove('tw-hidden')
    this.updateSubmitButtonState()
  }

  private async fetchAndPopulateCompanyData(
    skipResetOnFailure = false
  ): Promise<void> {
    try {
      const companyData = await this.fetchCompanyData()
      this.populateCompanyDetailsForm(companyData)
    } catch (error) {
      if (!skipResetOnFailure) {
        this.resetCompanyDetailsForm()
      }

      let errorMessage = useI18n().t(
        'wizard_v2.companies.set_admin_sync.company_data_failed'
      )

      const errorBody =
        error && typeof error === 'object' && 'body' in error
          ? error.body
          : null
      const errorText =
        errorBody && typeof errorBody === 'object' && 'error' in errorBody
          ? errorBody.error
          : null

      if (typeof errorText === 'string') {
        errorMessage = errorText
      }

      this.errorMessageTarget.textContent = errorMessage
      this.show(this.errorMessageWrapperTarget)
      this.show(this.companyDetailsFormTarget)
    }
  }

  private get fieldToInputMap(): Record<string, HTMLElement | undefined> {
    return {
      name: this.nameInputTarget,
      fileNumber: this.fileNumberInputTarget,
      organisationForm: this.organisationFormInputTarget,
      registrationNumber: this.registrationNumberInputTarget,
      vatNumber: this.vatNumberInputTarget,
      street: this.streetInputTarget,
      city: this.cityInputTarget,
      country: this.countryInputTarget,
      currency: this.currencyInputTarget,
    }
  }

  private resetCompanyDetailsForm(): void {
    Object.values(this.fieldToInputMap).forEach((inputElement) => {
      if (inputElement) {
        if (
          inputElement === this.countryInputTarget ||
          inputElement === this.currencyInputTarget
        ) {
          inputElement.classList.remove(
            'tw-cursor-not-allowed',
            'tw-pointer-events-none',
            'tw-bg-gray-100'
          )
        } else {
          ;(inputElement as HTMLInputElement).value = ''
          ;(inputElement as HTMLInputElement).readOnly = false
        }
      }
    })
  }

  private populateCompanyDetailsForm(
    companyData: Record<string, string>
  ): void {
    // Reset read-only states before populating
    Object.values(this.fieldToInputMap).forEach((input) => {
      if (!input) return
      if ('readOnly' in input) {
        ;(input as HTMLInputElement).readOnly = false
      }
    })

    for (const key in companyData) {
      if (Object.prototype.hasOwnProperty.call(companyData, key)) {
        const value = companyData[key]
        if (value !== null && String(value).trim() !== '') {
          const inputElement = this.fieldToInputMap[key]

          if (inputElement) {
            ;(inputElement as HTMLInputElement).value = String(value)
            if (
              inputElement === this.countryInputTarget ||
              inputElement === this.currencyInputTarget
            ) {
              // For the country select, add classes to make it look disabled but keep it submittable.
              inputElement.classList.add(
                'tw-cursor-not-allowed',
                'tw-pointer-events-none',
                'tw-bg-gray-100'
              )
            } else {
              // For other inputs, which are HTMLInputElement, set them to read-only.
              ;(inputElement as HTMLInputElement).readOnly = true
            }
          }
        }
      }
    }
    this.show(this.companyDetailsFormTarget)
  }

  private extractFormDetails(): {
    adminSyncId: string
    syncReference: string
  } {
    const formData = new FormData(this.adminSyncFormTarget as HTMLFormElement)
    const allAdminSyncIds = formData.getAll(
      'company[administration_sync_entity_id]'
    )
    const adminSyncId =
      allAdminSyncIds.length > 0
        ? (allAdminSyncIds[allAdminSyncIds.length - 1] as string)
        : ''
    const syncReference =
      (formData.get('company[administration_sync_reference]') as string) || ''

    return { adminSyncId, syncReference }
  }

  private getSelectedAdminSyncName(adminSyncId: string): string {
    if (adminSyncId === '') return 'None'
    const selector = `input[name="company[administration_sync_entity_id]"][value="${adminSyncId}"]`
    const selectedRadio = this.adminSyncFormTarget.querySelector(
      selector
    ) as HTMLInputElement
    if (
      selectedRadio &&
      selectedRadio.labels &&
      selectedRadio.labels.length > 0
    ) {
      return selectedRadio.labels[0].textContent?.trim() ?? ''
    }
    return ''
  }
}
